import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  getSheetTemplates, createNewSheetsInMilestone,
  getProjectSheets, addExistingSheetToMilestone, getMilestoneSheetRevisions,
  CARRY_FORWARD_TYPE_BY_MS_TYPE,
} from '../services/sheets.js'
import { resetSheetChecklist } from '../services/checklist.js'
import { getDiscipline } from './DrawingSheetList.jsx'
import { getFirst } from '../outline/utils.js'
import Select from './Select.jsx'

const DISCIPLINE_ORDER = [
  'General', 'Special General', 'As-Built', 'Architectural', 'Demolition',
  'Architectural Detailing', 'Interior Architecture', 'Energy', 'Civil',
  'Electrical', 'Mechanical', 'Structural', 'Landscape', 'Plumbing', 'Other',
]

/* ── Style tokens ───────────────────────────────────────────────────────── */

const S = {
  label: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text-muted)' },
  body:  { padding: '16px 20px', overflowY: 'auto' },
  footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderTop: '0.5px solid var(--aj-border)', flexShrink: 0 },
  btnPrimary: { background: '#07272D', border: 'none', borderRadius: 4, padding: '9px 20px', fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 500, color: '#fff', cursor: 'pointer' },
  btnSecondary: { background: 'transparent', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '8px 14px', fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-mid)', cursor: 'pointer' },
  btnDisabled: { background: 'var(--aj-bg-muted)', border: 'none', borderRadius: 4, padding: '9px 20px', fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 500, color: 'var(--aj-text-muted)', cursor: 'not-allowed' },
  input: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: '7px 10px', outline: 'none' },
  numInput: { fontFamily: '"DM Mono", monospace', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: '7px 8px', outline: 'none', width: 68 },
}

// Normalize a sheet number for case-insensitive uniqueness comparison.
function normNum(x) { return String(x || '').trim().toLowerCase() }

/* ── Sub-components ─────────────────────────────────────────────────────── */

function SectionLabel({ children }) {
  return <div style={{ ...S.label, marginBottom: 8 }}>{children}</div>
}

function Divider() {
  return <div style={{ borderBottom: '0.5px solid var(--aj-border)', margin: '14px 0' }} />
}

/* ── Revision type badge ─────────────────────────────────────────────────── */

// Short badge label/color per carry-forward Type — mirrors DrawingSheetList.jsx's REV_TYPE_STYLE
// (kept as its own small copy here rather than importing, same as this file's own DISCIPLINE_ORDER).
const CARRY_FORWARD_BADGE = {
  'Plan Check Revision': { label: 'PC REVISION', color: '#c87941' },
  'Permit Revision':     { label: 'PERMIT REV',  color: '#b8640a' },
  'ASI Revision':        { label: 'ASI',         color: '#6b4fa0' },
}

function RevBadge({ isExisting, type }) {
  const cf = CARRY_FORWARD_BADGE[type] || { label: 'ADVANCEMENT', color: '#1a6fa8' }
  return (
    <span style={{
      fontSize: 9, fontFamily: '"DM Mono", monospace', fontWeight: 700, flexShrink: 0,
      color: isExisting ? cf.color : '#1D9E75', letterSpacing: '0.04em',
    }}>
      {isExisting ? cf.label : 'ORIGINAL'}
    </span>
  )
}

/* ── Main component ─────────────────────────────────────────────────────── */

export default function AddSheetsWizard({ projectId, milestones, initialMilestoneId, onClose, onCreated, allowedDisciplines, newSheetsOnly }) {
  const [step, setStep] = useState(initialMilestoneId ? 2 : 1)

  // Step 1 state
  const [milestoneId, setMilestoneId] = useState(initialMilestoneId || null)

  // Step 2 state — tab toggle.
  // Default to 'existing' (carry-forward) unless newSheetsOnly is set (e.g. Existing Building 3D
  // Model — first-ever as-built sheets, no existing sheets to carry forward).
  const [step2Tab, setStep2Tab] = useState(newSheetsOnly ? 'new' : 'existing')   // 'existing' | 'new'

  // Step 2 — new sheets
  const [templates, setTemplates] = useState(null)  // null = not yet loaded
  const [selected, setSelected] = useState([])      // { key, templateId|null, sheetId?, isExisting?, sheetNumber, sheetTitle, discipline }
  const [customNum,        setCustomNum]        = useState('')
  const [customTitle,      setCustomTitle]      = useState('')
  const [customDisc,       setCustomDisc]       = useState(allowedDisciplines?.[0] || 'Architectural')
  const [customTemplateId, setCustomTemplateId] = useState('')
  const [customErr,        setCustomErr]        = useState(null)   // duplicate sheet-number message

  // Step 2 — existing sheets
  const [projectSheets,     setProjectSheets]     = useState(null)   // null = not loaded
  const [msExistingSheetIds, setMsExistingSheetIds] = useState(() => new Set())
  const existingLoadedForMs = useRef(null)  // milestoneId that was last loaded, or null

  // Step 3 / create state
  const [creating, setCreating] = useState(false)
  const [done,     setDone]     = useState(false)
  const [doneCount, setDoneCount] = useState(0)
  const [error,    setError]    = useState(null)

  /* Load new-sheet templates when entering step 2 */
  useEffect(() => {
    if (step !== 2 || templates !== null) return
    setTemplates([])  // show loading
    getSheetTemplates()
      .then(records => setTemplates(records))
      .catch(() => setTemplates([]))
  }, [step])

  /* Load project sheets on entering step 2 (needed for the Existing tab AND for
     duplicate sheet-number validation on new sheets). */
  useEffect(() => {
    if (step !== 2) return
    if (existingLoadedForMs.current === milestoneId) return  // already loaded for this milestone
    existingLoadedForMs.current = milestoneId

    setProjectSheets(null)
    setMsExistingSheetIds(new Set())

    Promise.all([
      getProjectSheets(projectId),
      milestoneId ? getMilestoneSheetRevisions(milestoneId) : Promise.resolve([]),
    ]).then(([allSheets, msRevisions]) => {
      // Collect sheet IDs already linked to this milestone
      const linkedIds = new Set(
        msRevisions.map(r => getFirst(r.fields['Drawing Sheet'])).filter(Boolean)
      )
      setMsExistingSheetIds(linkedIds)
      setProjectSheets(allSheets)
    }).catch(() => {
      setProjectSheets([])
    })
  }, [step, milestoneId]) // eslint-disable-line react-hooks/exhaustive-deps

  /* Derived */
  const activeMilestones = (milestones || []).filter(m => !m.fields['Posted Date'])
  const selectedMs = activeMilestones.find(m => m.id === milestoneId)
  const msLabel = selectedMs
    ? (selectedMs.fields['Full Deliverable Name'] || selectedMs.fields['Name'] || 'milestone')
    : 'milestone'
  const msDelType = getFirst(selectedMs?.fields?.['Name (from Deliverable)']) || ''

  const templateGroups = (templates || []).reduce((acc, tpl) => {
    const d = tpl.fields['Discipline'] || 'Other'
    if (allowedDisciplines && !allowedDisciplines.includes(d)) return acc
    const idx = acc.findIndex(g => g.discipline === d)
    if (idx >= 0) acc[idx].items.push(tpl)
    else acc.push({ discipline: d, items: [tpl] })
    return acc
  }, []).sort((a, b) => DISCIPLINE_ORDER.indexOf(a.discipline) - DISCIPLINE_ORDER.indexOf(b.discipline))

  const selectedGroups = DISCIPLINE_ORDER.reduce((acc, d) => {
    const items = selected.filter(s => s.discipline === d)
    if (items.length) acc.push({ discipline: d, items })
    return acc
  }, [])

  // Project sheets available to add as existing (exclude those already in this milestone or already selected)
  const availableExistingGroups = (projectSheets || []).length > 0
    ? DISCIPLINE_ORDER.reduce((acc, d) => {
        const items = (projectSheets || []).filter(s => {
          const title = s.fields['Sheet Number and Title'] || ''
          return getDiscipline(title) === d && !msExistingSheetIds.has(s.id)
        })
        if (items.length) acc.push({ discipline: d, items })
        return acc
      }, [])
    : []

  const isAdded          = tplId   => selected.some(s => !s.isExisting && s.templateId === tplId)
  const isExistingAdded  = sheetId => selected.some(s => s.isExisting  && s.sheetId   === sheetId)

  // ── Duplicate sheet-number validation (new sheets only) ───────────────────
  // A new sheet's number must not match any existing sheet in the project, nor any
  // other new sheet in this batch. (Brought-forward existing sheets are already unique.)
  const projectNumberSet = new Set((projectSheets || []).map(s => normNum(s.fields['Sheet Number'])).filter(Boolean))
  const newNums = selected.filter(s => !s.isExisting).map(s => normNum(s.sheetNumber))
  function isDupNumber(s) {
    if (s.isExisting) return false
    const n = normNum(s.sheetNumber)
    if (!n) return false
    if (projectNumberSet.has(n)) return true
    return newNums.filter(x => x === n).length > 1
  }
  // Any new sheet with an empty or duplicate number blocks the Review/Create step.
  const newSheetsInvalid = selected.some(s => !s.isExisting && (normNum(s.sheetNumber) === '' || isDupNumber(s)))

  /* Mutations */
  function addFromTemplate(tpl) {
    if (isAdded(tpl.id)) return
    setSelected(prev => [...prev, {
      key:        tpl.id,
      templateId: tpl.id,
      sheetNumber: tpl.fields['Sheet Number'] || '',
      sheetTitle:  tpl.fields['Sheet Name']   || '',
      discipline:  tpl.fields['Discipline']   || 'Other',
      isExisting:  false,
    }])
  }

  function addExistingSheet(sheet) {
    const sheetId = sheet.id
    if (isExistingAdded(sheetId)) return
    const title = sheet.fields['Sheet Number and Title'] || ''
    setSelected(prev => [...prev, {
      key:         'existing-' + sheetId,
      sheetId,
      isExisting:  true,
      templateId:  null,
      sheetNumber: sheet.fields['Sheet Number']  || '',
      sheetTitle:  sheet.fields['Sheet Title']   || '',
      discipline:  getDiscipline(title),
    }])
  }

  function removeSheet(key) {
    setSelected(prev => prev.filter(s => s.key !== key))
  }

  function updateSheet(key, field, val) {
    setSelected(prev => prev.map(s => s.key === key ? { ...s, [field]: val } : s))
  }

  function addCustom() {
    if (!customNum.trim() || !customTitle.trim()) return
    const n = normNum(customNum)
    if (projectNumberSet.has(n) || newNums.includes(n)) {
      setCustomErr('Sheet number “' + customNum.trim() + '” already exists in this project.')
      return
    }
    setSelected(prev => [...prev, {
      key:        'custom-' + Date.now(),
      templateId: customTemplateId || null,
      sheetNumber: customNum.trim(),
      sheetTitle:  customTitle.trim(),
      discipline:  customDisc,
      isExisting:  false,
    }])
    setCustomNum(''); setCustomTitle(''); setCustomErr(null)
    // Keep discipline + template — likely the same for adjacent custom sheets in a batch
  }

  /* Create */
  async function handleCreate() {
    setCreating(true); setError(null)
    try {
      const newSheets      = selected.filter(s => !s.isExisting)
      const existingSheets = selected.filter(s =>  s.isExisting)
      let totalCount = 0

      // New sheets: records + Original Release revisions + checklist, through the one shared
      // path (AlderCAD's draft sheets use it too). Sheets carry no assignment of their own —
      // crews are made on the Flight Board, on the milestone (docs/FLOW_RECONCILIATION_PLAN.md,
      // RASCI retired 2026-09-02).
      let newIds = []
      if (newSheets.length > 0) {
        newIds = await createNewSheetsInMilestone(projectId, milestoneId, newSheets)
        totalCount += newIds.length
      }

      // Existing sheets: carry-forward revision per sheet (Type defaults to the milestone's own
      // revision kind — Plan Check Revision / Permit Revision — or Phase Advancement otherwise;
      // see addExistingSheetToMilestone's CARRY_FORWARD_TYPE_BY_MS_TYPE)
      for (const s of existingSheets) {
        await addExistingSheetToMilestone(s.sheetId, milestoneId, msDelType)
        totalCount++
      }

      // Seed each carried-forward sheet's checklist too (new sheets got theirs above).
      // resetSheetChecklist is idempotent (creates missing, skips existing), so this self-heals
      // any existing sheet that never got one. No-ops for sheets with no template. Best-effort:
      // a checklist failure shouldn't unwind sheets already created.
      const clIds = existingSheets.map(s => s.sheetId)
      await Promise.all(clIds.map(id => resetSheetChecklist(id)))
        .catch(err => console.warn('[AddSheets] checklist seeding error:', err))

      // Reference/Production > Drawing Sheets caches each sheet's revisions in ctx.sheetRevCache
      // and only ever refetches on this event (see the 'sheet-revisions-stale' listener in
      // DrawingSheetList.jsx) — without it, a sheet already visited in that view keeps showing
      // its pre-existing revision list (missing the new in-progress one) until the milestone is
      // posted/unposted or the page is hard-reloaded. Mirrors PostMilestoneModal.jsx's dispatch.
      const touchedSheetIds = [...newIds, ...existingSheets.map(s => s.sheetId)]
      if (touchedSheetIds.length > 0) {
        window.dispatchEvent(new CustomEvent('sheet-revisions-stale', { detail: { sheetIds: touchedSheetIds, msId: milestoneId } }))
      }

      setDoneCount(totalCount)
      setDone(true)
      if (onCreated) onCreated(totalCount)
    } catch (err) {
      setError(err.message || 'Something went wrong')
    } finally {
      setCreating(false)
    }
  }

  /* Backdrop click no longer closes the wizard — use Cancel/Close (site standard). */
  function handleOverlayClick() { /* intentionally no-op */ }

  /* ── Render helpers ───────────────────────────────────────────────────── */

  function renderStep1() {
    return <>
      <div style={S.body}>
        <SectionLabel>Choose a milestone for these sheets</SectionLabel>
        {activeMilestones.length === 0 && (
          <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-muted)' }}>No active milestones</div>
        )}
        {activeMilestones.map(ms => {
          const sel = milestoneId === ms.id
          return (
            <div
              key={ms.id}
              onClick={() => setMilestoneId(ms.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 6, cursor: 'pointer', background: sel ? 'var(--aj-bg-subtle)' : 'transparent', border: sel ? '0.5px solid var(--aj-border-mid)' : '0.5px solid transparent', marginBottom: 4 }}
            >
              <div style={{ width: 14, height: 14, borderRadius: '50%', border: `1.5px solid ${sel ? 'var(--aj-text)' : 'var(--aj-border-mid)'}`, background: sel ? 'var(--aj-text)' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {sel && <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--aj-bg-elevated)' }} />}
              </div>
              <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text)' }}>
                {ms.fields['Full Deliverable Name'] || ms.fields['Name']}
              </span>
            </div>
          )
        })}
      </div>
      <div style={S.footer}>
        <span />
        <button
          onClick={() => setStep(2)}
          disabled={!milestoneId}
          style={milestoneId ? S.btnPrimary : S.btnDisabled}
        >Next</button>
      </div>
    </>
  }

  function renderStep2() {
    const canAdd = customNum.trim() && customTitle.trim()

    // Tab toggle styles
    const tabBase = {
      fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 500,
      padding: '5px 14px', borderRadius: 4, cursor: 'pointer', border: 'none',
    }
    const tabActive   = { ...tabBase, background: '#07272D', color: '#fff' }
    const tabInactive = { ...tabBase, background: 'transparent', color: 'var(--aj-text-muted)', border: '0.5px solid var(--aj-border-mid)' }

    return <>
      <div style={{ ...S.body, paddingBottom: 0 }}>

        {/* Educational banner — hidden when newSheetsOnly (no existing sheets to carry forward) */}
        {!newSheetsOnly && (
          <div style={{
            display: 'flex', gap: 9, alignItems: 'flex-start',
            background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)',
            borderRadius: 6, padding: '9px 11px', marginBottom: 14,
          }}>
            <span style={{ fontSize: 13, lineHeight: '17px', flexShrink: 0 }}>📐</span>
            <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, lineHeight: 1.45, color: 'var(--aj-text-mid)' }}>
              Adding a sheet to a milestone issues a new version of that sheet at this milestone.
              If the sheet already exists in the project, <strong style={{ color: 'var(--aj-text)' }}>carry it forward</strong> from
              Existing Sheets (a Phase Advancement) instead of re-creating it. Only use New Sheets for
              sheets that don&rsquo;t exist in the project yet (an Original Release).
            </span>
          </div>
        )}

        {/* Tab toggle — hidden when newSheetsOnly (only new/original-release sheets allowed) */}
        {!newSheetsOnly && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
            <button style={step2Tab === 'existing' ? tabActive : tabInactive} onClick={() => setStep2Tab('existing')}>Existing Sheets</button>
            <button style={step2Tab === 'new' ? tabActive : tabInactive} onClick={() => setStep2Tab('new')}>New Sheets</button>
          </div>
        )}

        {/* Selected summary (shared across both tabs) */}
        {selected.length > 0 && <>
          <SectionLabel>Selected ({selected.length})</SectionLabel>
          {selectedGroups.map(({ discipline, items }) => (
            <div key={discipline} style={{ marginBottom: 10 }}>
              <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{discipline}</div>
              {items.map(s => (
                <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                  {s.isExisting ? (
                    <>
                      {/* Existing sheets: show read-only with type badge */}
                      <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 12, color: 'var(--aj-text-muted)', minWidth: 52 }}>
                        {s.sheetNumber}
                      </span>
                      <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-mid)', flex: 1 }}>{s.sheetTitle}</span>
                      <RevBadge isExisting={true} type={CARRY_FORWARD_TYPE_BY_MS_TYPE[msDelType]} />
                    </>
                  ) : (() => {
                    const dup = isDupNumber(s)
                    return (
                    <>
                      <input
                        value={s.sheetNumber}
                        onChange={e => updateSheet(s.key, 'sheetNumber', e.target.value)}
                        style={{ ...S.numInput, ...(dup ? { borderColor: '#dc2626', background: 'rgba(192,57,43,0.14)' } : {}) }}
                        placeholder="A-101"
                        title={dup ? 'Duplicate sheet number — already exists in this project' : ''}
                      />
                      <input
                        value={s.sheetTitle}
                        onChange={e => updateSheet(s.key, 'sheetTitle', e.target.value)}
                        style={{ ...S.input, flex: 1, minWidth: 0 }}
                        placeholder="Sheet Title"
                      />
                      {dup && <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, color: '#dc2626', flexShrink: 0 }}>duplicate</span>}
                    </>
                    )
                  })()}
                  <button
                    onClick={() => removeSheet(s.key)}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', padding: '4px 6px', fontSize: 14, lineHeight: 1 }}
                    title="Remove"
                  >✕</button>
                </div>
              ))}
            </div>
          ))}
          <Divider />
        </>}

        {/* ── NEW SHEETS TAB ─────────────────────────────────────────────── */}
        {step2Tab === 'new' && <>
          {/* Template catalog */}
          <SectionLabel>Templates</SectionLabel>
          {templates === null && (
            <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-muted)', padding: '8px 0' }}>Loading templates...</div>
          )}
          {templates !== null && templates.length === 0 && (
            <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-muted)' }}>No templates found</div>
          )}
          {templateGroups.map(({ discipline, items }) => (
            <div key={discipline} style={{ marginBottom: 12 }}>
              <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{discipline}</div>
              {items.map(tpl => {
                const added = isAdded(tpl.id)
                return (
                  <div
                    key={tpl.id}
                    onClick={() => addFromTemplate(tpl)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 4, cursor: added ? 'default' : 'pointer', background: added ? 'rgba(29,158,117,0.05)' : 'transparent', marginBottom: 2, opacity: added ? 0.7 : 1 }}
                  >
                    <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 12, color: 'var(--aj-text-muted)', minWidth: 52 }}>
                      {tpl.fields['Sheet Number']}
                    </span>
                    <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text)', flex: 1 }}>
                      {tpl.fields['Sheet Name']}
                    </span>
                    <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: added ? '#1D9E75' : '#1a6fa8', flexShrink: 0 }}>
                      {added ? 'Added' : '+ Add'}
                    </span>
                  </div>
                )
              })}
            </div>
          ))}

          {/* Custom sheet entry */}
          <Divider />
          <SectionLabel>Custom sheet</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* Row 1: Number + Title */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                value={customNum}
                onChange={e => { setCustomNum(e.target.value); if (customErr) setCustomErr(null) }}
                onKeyDown={e => e.key === 'Enter' && addCustom()}
                style={{ ...S.numInput, ...(customErr ? { borderColor: '#dc2626', background: 'rgba(192,57,43,0.14)' } : {}) }}
                placeholder="A-101"
              />
              <input
                value={customTitle}
                onChange={e => setCustomTitle(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addCustom()}
                style={{ ...S.input, flex: 1, minWidth: 120 }}
                placeholder="Sheet Title"
              />
            </div>
            {/* Row 2: Discipline + Template + Add */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <Select
                value={customDisc}
                onChange={setCustomDisc}
                options={(allowedDisciplines ? DISCIPLINE_ORDER.filter(d => allowedDisciplines.includes(d)) : DISCIPLINE_ORDER).map(d => ({ value: d, label: d }))}
                searchable
                style={{ padding: '7px 8px', fontSize: 12 }}
              />
              <Select
                value={customTemplateId}
                onChange={setCustomTemplateId}
                options={[
                  { value: '', label: '— No template —' },
                  ...(templates || [])
                    .filter(t => !allowedDisciplines || allowedDisciplines.includes(t.fields['Discipline'] || 'Other'))
                    .map(t => ({
                      value: t.id,
                      label: (t.fields['Sheet Number'] ? t.fields['Sheet Number'] + ' — ' : '') + (t.fields['Sheet Name'] || ''),
                    })),
                ]}
                searchable
                placeholder="Template (optional)"
                style={{ flex: 1, padding: '7px 8px', fontSize: 12 }}
              />
              <button
                onClick={addCustom}
                disabled={!canAdd}
                style={canAdd ? S.btnSecondary : S.btnDisabled}
              >Add</button>
            </div>
          </div>
          {customErr && <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#dc2626', marginTop: 6 }}>{customErr}</div>}
        </>}

        {/* ── EXISTING SHEETS TAB ─────────────────────────────────────────── */}
        {step2Tab === 'existing' && <>
          {projectSheets === null && (
            <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-muted)', padding: '8px 0' }}>Loading project sheets...</div>
          )}
          {projectSheets !== null && availableExistingGroups.length === 0 && (
            <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-muted)' }}>
              {(projectSheets || []).length === 0
                ? 'No drawing sheets in this project yet'
                : 'All project sheets are already in this milestone'}
            </div>
          )}
          {availableExistingGroups.map(({ discipline, items }) => (
            <div key={discipline} style={{ marginBottom: 12 }}>
              <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{discipline}</div>
              {items.map(sheet => {
                const added = isExistingAdded(sheet.id)
                return (
                  <div
                    key={sheet.id}
                    onClick={() => addExistingSheet(sheet)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 4, cursor: added ? 'default' : 'pointer', background: added ? 'rgba(26,111,168,0.05)' : 'transparent', marginBottom: 2, opacity: added ? 0.7 : 1 }}
                  >
                    <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 12, color: 'var(--aj-text-muted)', minWidth: 52 }}>
                      {sheet.fields['Sheet Number'] || '—'}
                    </span>
                    <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text)', flex: 1 }}>
                      {sheet.fields['Sheet Title'] || sheet.fields['Sheet Number and Title'] || 'Untitled'}
                    </span>
                    <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: added ? '#1D9E75' : '#1a6fa8', flexShrink: 0 }}>
                      {added ? 'Added' : '+ Add'}
                    </span>
                  </div>
                )
              })}
            </div>
          ))}
        </>}

        {/* Bottom padding */}
        <div style={{ height: 16 }} />
      </div>
      <div style={S.footer}>
        <button onClick={() => initialMilestoneId ? onClose() : setStep(1)} style={S.btnSecondary}>Back</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {newSheetsInvalid && <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#dc2626' }}>Resolve duplicate / empty sheet numbers</span>}
          <button
            onClick={() => setStep(3)}
            disabled={selected.length === 0 || newSheetsInvalid}
            style={(selected.length > 0 && !newSheetsInvalid) ? S.btnPrimary : S.btnDisabled}
          >
            Review {selected.length > 0 ? `(${selected.length})` : ''}
          </button>
        </div>
      </div>
    </>
  }

  function renderStep3() {
    const newCount      = selected.filter(s => !s.isExisting).length
    const existingCount = selected.filter(s =>  s.isExisting).length
    const carryForwardLabel = CARRY_FORWARD_TYPE_BY_MS_TYPE[msDelType] || 'Phase Advancement'

    let summaryText
    if (newCount > 0 && existingCount > 0) {
      summaryText = (
        <>
          Adding <strong style={{ color: 'var(--aj-text)' }}>{newCount} new</strong> sheet{newCount !== 1 ? 's' : ''} (Original Release)
          {' '}and <strong style={{ color: 'var(--aj-text)' }}>{existingCount} existing</strong> sheet{existingCount !== 1 ? 's' : ''} ({carryForwardLabel}) to{' '}
          <strong style={{ color: 'var(--aj-text)' }}>{msLabel}</strong>
        </>
      )
    } else if (existingCount > 0) {
      summaryText = (
        <>
          Adding <strong style={{ color: 'var(--aj-text)' }}>{existingCount} existing sheet{existingCount !== 1 ? 's' : ''}</strong> as {carryForwardLabel} to{' '}
          <strong style={{ color: 'var(--aj-text)' }}>{msLabel}</strong>
        </>
      )
    } else {
      summaryText = (
        <>
          Creating <strong style={{ color: 'var(--aj-text)' }}>{newCount} sheet{newCount !== 1 ? 's' : ''}</strong> as Original Release linked to{' '}
          <strong style={{ color: 'var(--aj-text)' }}>{msLabel}</strong>
        </>
      )
    }

    return <>
      <div style={S.body}>
        {error && (
          <div style={{ background: 'rgba(192,57,43,0.14)', border: '0.5px solid rgba(192,57,43,0.3)', borderRadius: 4, padding: '10px 12px', marginBottom: 14, fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#c0392b' }}>
            {error}
          </div>
        )}
        <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-mid)', marginBottom: 16, lineHeight: 1.5 }}>
          {summaryText}
        </div>
        {selectedGroups.map(({ discipline, items }) => (
          <div key={discipline} style={{ marginBottom: 12 }}>
            <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{discipline}</div>
            {items.map(s => (
              <div key={s.key} style={{ display: 'flex', gap: 10, padding: '3px 0', alignItems: 'center' }}>
                <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 12, color: 'var(--aj-text-muted)', minWidth: 52 }}>{s.sheetNumber}</span>
                <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text)', flex: 1 }}>{s.sheetTitle}</span>
                <RevBadge isExisting={s.isExisting} type={CARRY_FORWARD_TYPE_BY_MS_TYPE[msDelType]} />
              </div>
            ))}
          </div>
        ))}

      </div>
      <div style={S.footer}>
        {creating ? (
          <>
            <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-muted)' }}>Adding sheets...</span>
            <span />
          </>
        ) : <>
          <button onClick={() => { setError(null); setStep(2) }} style={S.btnSecondary}>Back</button>
          <button onClick={handleCreate} style={S.btnPrimary}>
            Add {selected.length} sheet{selected.length !== 1 ? 's' : ''}
          </button>
        </>}
      </div>
    </>
  }

  function renderDone() {
    const newCount      = selected.filter(s => !s.isExisting).length
    const existingCount = selected.filter(s =>  s.isExisting).length
    const carryForwardLabel = CARRY_FORWARD_TYPE_BY_MS_TYPE[msDelType] || 'Phase Advancement'
    const subtitle = newCount > 0 && existingCount > 0
      ? `${newCount} new (Original Release) + ${existingCount} existing (${carryForwardLabel}) — ${msLabel}`
      : existingCount > 0
        ? `${carryForwardLabel} — ${msLabel}`
        : `Original Release — ${msLabel}`

    return (
      <div style={{ ...S.body, textAlign: 'center', padding: '32px 24px' }}>
        <div style={{ fontSize: 32, marginBottom: 12, color: '#1D9E75' }}>
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <circle cx="20" cy="20" r="18" stroke="#1D9E75" strokeWidth="2"/>
            <polyline points="12,21 18,27 29,14" stroke="#1D9E75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 16, fontWeight: 600, color: 'var(--aj-text)', marginBottom: 6 }}>
          {doneCount} sheet{doneCount !== 1 ? 's' : ''} added
        </div>
        <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-muted)', marginBottom: 24 }}>
          {subtitle}
        </div>
        <button onClick={onClose} style={S.btnPrimary}>Done</button>
      </div>
    )
  }

  /* ── Portal render ──────────────────────────────────────────────────────── */

  return createPortal(
    <div
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={handleOverlayClick}
    >
      <div style={{ background: 'var(--aj-bg-elevated)', borderRadius: 12, border: '0.5px solid var(--aj-border)', width: 520, maxWidth: '100%', maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 20px', borderBottom: '0.5px solid var(--aj-border)', flexShrink: 0 }}>
          <div style={{ flex: 1 }}>
            {!done && (
              <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 9, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 2 }}>
                {initialMilestoneId ? (step === 2 ? 'Step 1 of 2' : 'Step 2 of 2') : `Step ${step} of 3`}
              </div>
            )}
            <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {done ? 'Sheets Added'
                : step === 1 ? 'Add Drawing Sheets — Choose Milestone'
                : step === 2 ? 'Add Drawing Sheets — Select Sheets'
                : 'Add Drawing Sheets — Review'}
            </div>
            {!done && initialMilestoneId && (
              <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 2 }}>
                {msLabel}
              </div>
            )}
          </div>
          <button
            onClick={() => !creating && onClose()}
            style={{ background: 'transparent', border: 'none', cursor: creating ? 'not-allowed' : 'pointer', color: 'var(--aj-text-muted)', padding: 4 }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <line x1="3" y1="3" x2="13" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="13" y1="3" x2="3" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Body — scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {done        ? renderDone()  :
           step === 1  ? renderStep1() :
           step === 2  ? renderStep2() :
                         renderStep3()}
        </div>

      </div>
    </div>,
    document.body
  )
}
